/**
 * Inserts markdown paragraph breaks before common clinical section headers
 * found in LLM-generated chronology descriptions.
 *
 * Transforms patterns like ". Vital signs:" into ".\n\n**Vital signs:**"
 * so react-markdown renders them as separate paragraphs with bold headers.
 *
 * Safe for use with inline citation markers ([1], [2], etc.).
 */

const SECTION_HEADERS = [
  // Clinical examination
  'Vital signs',
  'Physical examination',
  'Examination findings',
  'Cardiovascular examination',
  'Respiratory examination',
  'Neurological examination',
  'Abdominal examination',
  'On examination',
  'Systems review',
  'Clinical findings',
  'Examination',

  // Diagnostics
  'Laboratory results',
  'Investigation results',
  'Imaging results',
  'Blood results',
  'Lab results',
  'Blood tests',
  'Investigations',
  'Echocardiogram',
  'Histology',
  'Pathology',
  'Microbiology',
  'Radiology',
  'Imaging',
  'Bloods',
  'ECG',

  // Clinical reasoning
  'Clinical impression',
  'Differential diagnosis',
  'Working diagnosis',
  'Problem list',
  'Assessment',
  'Impression',
  'Diagnosis',

  // Treatment
  'Discharge medications',
  'Medication changes',
  'Management plan',
  'Treatment plan',
  'Prescriptions',
  'Medications',
  'Management',
  'Treatment',
  'Plan',

  // Recommendations / follow-up
  'Safety netting',
  'Patient advice',
  'Recommendations',
  'Follow-up',
  'Follow up',
  'Referrals',
  'Referral',
  'Advice',

  // Admission / Discharge
  'History of presenting complaint',
  'Presenting complaint',
  'Discharge summary',
  'Admission details',
  'Past medical history',
  'Discharge plan',
  'Allergy status',
  'Social history',
  'Family history',
  'Drug history',

  // Observations & outcomes
  'Clinical observations',
  'Nursing observations',
  'Complications',
  'Observations',
  'Prognosis',
  'Progress',
  'Outcome',
];

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Sort by length descending so longer headers match first (e.g. "Laboratory results" before "Lab results")
const sortedHeaders = [...SECTION_HEADERS].sort((a, b) => b.length - a.length);
const headerPattern = sortedHeaders.map(escapeRegex).join('|');

// Matches: sentence boundary (. or ;) with optional citation markers, then a known header + colon
const sectionRegex = new RegExp(
  `([.;](?:\\s*\\[\\d+\\])*)\\s+(${headerPattern})\\s*:`,
  'gi',
);

// Matches: a known header + colon at the very start of the string
const startRegex = new RegExp(`^(${headerPattern})\\s*:`, 'i');

export function formatClinicalDescription(text: string): string {
  if (!text) return text;

  // Insert paragraph breaks before section headers after sentence boundaries
  let result = text.replace(sectionRegex, (_match, boundary: string, header: string) => {
    return `${boundary}\n\n**${header}:**`;
  });

  // Bold a header at the very start of the string (no paragraph break needed)
  result = result.replace(startRegex, (_match, header: string) => {
    return `**${header}:**`;
  });

  // Collapse triple+ newlines to double
  result = result.replace(/\n{3,}/g, '\n\n');

  return result;
}
